package meineglasfaser.frontend.www.pages.portal.auth

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.Anchor
import io.ktor.client.plugins.*
import io.ktor.http.*
import meineglasfaser.api.CustomerType
import meineglasfaser.api.routes.AuthRoutes.RealmCustomerPortal.Register
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.Toast
import meineglasfaser.frontend.common.components.widgets.Toasts
import meineglasfaser.frontend.common.components.yaform.*
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import meineglasfaser.frontend.common.withExistingParams
import meineglasfaser.frontend.www.components.CustomerState
import meineglasfaser.frontend.www.components.CustomerStateRedirectIfLoggedIn
import meineglasfaser.frontend.www.components.GlobalCustomerState
import meineglasfaser.frontend.www.components.sections.FooterLinks
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*

@Page
@Layout(".components.layouts.ShopLayout")
@Composable
fun RegisterPage(ctx: PageContext) {
    CustomerStateRedirectIfLoggedIn()

    Div({ classes("auth-form")}) {
        H1({ classes("text-center") }) {
            Text("Registrierung")
        }
        YaForm(
            onSubmitScope = { data ->
                val body = Register.RequestBody(
                    type = data.string(Register.RequestBody::type).let { CustomerType.valueOf(it) },
                    firstName = data.stringOrNull(Register.RequestBody::firstName),
                    lastName = data.stringOrNull(Register.RequestBody::lastName),
                    companyName = data.stringOrNull(Register.RequestBody::companyName),
                    vatNumber = data.stringOrNull(Register.RequestBody::vatNumber),
                    email = data.string(Register.RequestBody::email),
                    password = data.string(Register.RequestBody::password),
                    passwordRepeat = data.string(Register.RequestBody::passwordRepeat),
                    tosAgreement = data.boolean(Register.RequestBody::tosAgreement),
                    gdprAgreement = data.boolean(Register.RequestBody::gdprAgreement),
                )
                BackendApi.post(Register()) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        GlobalCustomerState.value = CustomerState.Indeterminate
                        Toasts.display(
                            Toast.success("Erfolgreich registriert")
                        )
                        val redirectUrl = ctx.route.params["redirect"]
                        if (redirectUrl != null) {
                            ctx.router.navigateTo(redirectUrl)
                        } else {
                            ctx.router.navigateTo("/kundenportal/overview")
                        }
                    },
                    onFailure = { error ->
                        when (error) {
                            is ClientRequestException -> when (error.response.status) {
                                HttpStatusCode.BadRequest -> {
                                    Toasts.display(
                                        Toast.error("Bitte E-Mail und Passwort angeben")
                                    )
                                }
                                HttpStatusCode.Unauthorized -> {
                                    Toasts.display(
                                        Toast.error("E-Mail und/oder Passwort falsch")
                                    )
                                }
                                HttpStatusCode.Forbidden -> {
                                    Toasts.display(
                                        Toast.error("Die angegebene E-Mail-Adresse wird bereits verwendet")
                                    )
                                }
                                else -> {
                                    Toasts.display(
                                        Toast.error("Unerwarteter Fehler: ${error.response.status}")
                                    )
                                }
                            }
                            else -> {
                                Toasts.display(
                                    Toast.error("Unerwarteter Fehler: ${error.message}")
                                )
                            }
                        }
                    }
                )
            }
        ) {
            val type = state.data[Register.RequestBody::type.name]?.let { typeStr ->
                remember(typeStr) { CustomerType.entries.find { it.name == typeStr.toString() } }
            }
            P({ classes("text-center") }) {
                Span { Text("Sie haben bereits ein Konto? ") }
                val href = ctx.route.withExistingParams("/kundenportal/auth/login")
                Anchor(href = href, {
                }) {
                    Text("Hier anmelden")
                }
            }
            YaFormDropdown(
                prop = Register.RequestBody::type,
                label = { Text("Ich bin...") },
                options = listOf(
                    CustomerType.INDIVIDUAL to "Privatkunde",
                    CustomerType.COMPANY to "Geschäftskunde",
                ),
                required = true,
            )
            if (type == CustomerType.INDIVIDUAL) {
                YaFormTextInput(
                    prop = Register.RequestBody::firstName,
                    label = { Text("Vorname") },
                    type = InputType.Text,
                    required = true,
                )
                YaFormTextInput(
                    prop = Register.RequestBody::lastName,
                    label = { Text("Nachname") },
                    type = InputType.Text,
                    required = true,
                )
            } else if (type == CustomerType.COMPANY) {
                YaFormTextInput(
                    prop = Register.RequestBody::companyName,
                    label = { Text("Firmenname") },
                    type = InputType.Text,
                    required = true,
                )
                YaFormTextInput(
                    prop = Register.RequestBody::vatNumber,
                    label = { Text("Umsatzsteuer-ID") },
                    type = InputType.Text,
                    required = true,
                )
            }
            YaFormTextInput(
                prop = Register.RequestBody::email,
                type = InputType.Email,
                label = { Text("E-Mail") },
                required = true,
                disabled = type == null,
            )
            YaFormTextInput(
                prop = Register.RequestBody::password,
                type = InputType.Password,
                required = true,
                disabled = type == null,
                label = { Text("Passwort") },
            )
            YaFormTextInput(
                prop = Register.RequestBody::passwordRepeat,
                label = { Text("Passwort (Wiederholung)") },
                type = InputType.Password,
                required = true,
                disabled = type == null,
            )
            Fieldset {
                YaFormCheckInput(
                    prop = Register.RequestBody::tosAgreement,
                    label = {
                        Text("Ich habe die ")
                        Anchor(FooterLinks.TERMS_OF_SERVICE_URL.href) {
                            Text("Allgemeinen Geschäftsbedingungen")
                        }
                        Text(" gelesen und akzeptiere diese")
                    },
                    required = true,
                    disabled = type == null,
                )
                YaFormCheckInput(
                    prop = Register.RequestBody::gdprAgreement,
                    label = {
                        Text("Ich habe die Datenschutzerklärung (in den AGBs) gelesen und akzeptiere diese")
                    },
                    required = true,
                    disabled = type == null,
                )
            }
            YaFormButton(
                type = ButtonType.Submit,
                disabled = type == null,
            ) {
                Text("Registrieren")
            }
        }
    }
}
